<template>
  <div>
    <div
      :ref="tagRefName"
      tabindex="0"
      @blur="isDropdownInFocus = false"
      @focus="isDropdownInFocus = true"
    >
      <p
        class="pb-1"
        style="margin: 0; font-size: 14px; color: black"
      >
        <b>{{ label || $ts('formTagsSelector.defaultLabel') }}</b>
        <RsPartialHelpTooltip
          class="ml-2"
          tooltip-position="right"
          :triggers="['hover']"
        >
          {{ $ts('formTagsSelector.tooltipHint') }}
        </RsPartialHelpTooltip>
      </p>

      <RsFormTagsInputV2
        :placeholder="placeholder || $ts('formTagsSelector.defaultPlaceholder')"
        :value="currentTagsAsVoerro"
        :add-tags-on-space="false"
        :add-tags-on-comma="true"
        :add-tags-on-blur="false"
        :disabled="disabled || isLoading"
        @on-search="handleSearch"
        @on-tags-changed="handleTagsChanged"
        @on-focus="handleFocus"
        @on-blur="handleBlur"
      />

      <div
        v-show="(isDropdownInFocus || isInputInFocus) && tagHits.length"
        ref="dropdown"
        class="p-2 mb-3"
        style="background: #F7F7F9; border: 1.3px solid #DBDBDB; border-radius: 4px; margin-top: 8px; max-height: 14rem; overflow-y: scroll"
        @click="handleInputFocus()"
      >
        <p style="color: #C2C6DA; font-size: 14.3px; margin: 0; display: flex">
          {{ $ts('formTagsSelector.frequentlyUsedTags') }} <RsSpin
            v-if="isLoading"
            style="font-size: 6px; margin-left: 10px"
          />
        </p>
        <p
          v-for="tag in tagHits"
          :key="tag.id || tag.name"
          class="unselected-tag"
          @click="handleAddExistingTag(tag)"
        >
          <RsIcon
            icon="plus-circle"
            weight="fas"
            style="color: #7D59E1; font-size: 15px "
          />
          <span style="margin-left: 6px; color: #9195AC;font-size: 14px;">{{ tag.name }}</span>
        </p>
      </div>
    </div>
    <!-- Show Translated Assigned Tags -->
    <div
      v-if="$rsGlobals.$siteSourceClientLanguages.length"
      class="text-right"
    >
      <span
        class="text-sm cursor-pointer rs-text-secondary-light font-weight-bold"
        @click="isTagTranslationsModalOpen = true"
      >
        <i class="far fa-language" /> {{ $ts('formTagsSelector.translateAssignedTags') }}
      </span>
      <RsModalTagTranslations
        v-model="isTagTranslationsModalOpen"
        :tags="filteredSavedTags"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ITags } from '@llws/lift-entity-interfaces/dist/lift';
import { IObjectsHasTags } from '@llws/lift-entity-interfaces';
import { Equal, convertCommandToQueryString } from '@llws/dr-objectified-query';
import RsPartialHelpTooltip from '@/components/RsPartialHelpTooltip.vue';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { TVoerroTagsInputVal } from './RsFormTagsInputV2.vue';
import RsModalTagTranslations from '@/components/RsModalTagTranslations.vue';

export default defineComponent({
  name: 'RsFormTagsSelector',

  components: {
    RsPartialHelpTooltip,
    RsModalTagTranslations,
  },

  props: {
    tagModel: {
      type: String,
      required: true,
    },

    tagModelId: {
      type: Number,
      required: true,
    },

    label: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },
    savedTags: {
      type: Array as () => (ITags | undefined | null)[],
      default: () => [],
    },
  },

  data() {
    return {
      tags: [] as ITags[],
      objectHasTags: [] as IObjectsHasTags[],
      removeList: [] as IObjectsHasTags[],
      openDropdown: false as boolean,
      tagRefName: 'tagContainer',
      isLoading: false as boolean,
      searchText: '' as string,
      isInputInFocus: false as boolean,
      isDropdownInFocus: false as boolean,
      isTagTranslationsModalOpen: false as boolean,
    };
  },

  computed: {
    filteredSavedTags(): ITags[] {
      return this.savedTags.filter((t): t is ITags => !!t);
    },

    currentTagsAsVoerro(): TVoerroTagsInputVal[] {
      return this.objectHasTags
        .map((oht) => ({ key: oht.tagId ? `${oht.tagId}` : '0', value: oht.tag?.name ?? '' }))
        .filter((tag) => tag.value.length > 0);
    },

    tagHits(): ITags[] {
      return this.tags
        .filter((t) => (
          !this.objectHasTags.some((oht) => oht.tag?.name.trim() === t.name.trim())
          && t.name.toLowerCase().includes(this.searchText.toLowerCase())
          && t.name.trim().length > 0
        ));
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      try {
        this.isLoading = true;
        this.removeList = [];

        const tagsQuery = convertCommandToQueryString({
          where: [ Equal('clientId', this.$rsGlobals.$currentClient.id) ],
          limit: 9999,
        });
        this.tags = (await adminRequest.get<ITags[]>(`/website-api/tags${tagsQuery}`)).data ?? [];

        const ohtQuery = convertCommandToQueryString({
          where: [ Equal('model', this.tagModel), Equal('objectId', this.tagModelId) ],
          relations: [{ path: 'tag', alias: 't' }],
          limit: 9999,
        });
        this.objectHasTags = (await adminRequest.get<IObjectsHasTags[]>(`/website-api/objects_has_tags${ohtQuery}`)).data ?? [];

        this.handleSearch('');
      } finally {
        this.isLoading = false;
      }
    },

    handleBlur() {
      this.isInputInFocus = false;
    },

    handleFocus() {
      this.isInputInFocus = true;
    },

    handleSearch(searchText: string) {
      this.searchText = searchText;
    },

    handleInputFocus() {
      if ((this.$refs[this.tagRefName] as HTMLElement)?.getElementsByTagName('input').item(0)) {
        (this.$refs[this.tagRefName] as HTMLElement).getElementsByTagName('input').item(0)!.focus();
        this.isInputInFocus = true;
      }
    },

    handleAddExistingTag(tag: ITags) {
      this.objectHasTags = [
        ...this.objectHasTags,
        {
          tag,
          tagId: tag.id!,
          objectId: this.tagModelId,
          model: this.tagModel,
          createdUserId: Number(this.$rsGlobals.$currentUser.userId),
        },
      ];
    },

    handleTagsChanged(values: TVoerroTagsInputVal[]) {
      const tagsToRemove = this.objectHasTags
        .filter((oht) => !values.some((v) => oht.tag?.name.toLowerCase() === v.value.toLowerCase()));

      this.removeList = [ ...this.removeList, ...tagsToRemove.filter((oht) => oht.id) ];

      this.objectHasTags = values.map((v) => {
        const existingOHT = this.objectHasTags.find((oht) => oht.tag?.name.toLowerCase() === v.value.toLowerCase());

        if (existingOHT) {
          return existingOHT;
        }

        const existingTag = this.tags.find((t) => t.name.toLowerCase() === v.value.toLowerCase());
        const siteId = this.$rsGlobals.$currentClient.sites?.find((s) => s.sourceClientId === this.$rsGlobals.$currentClient.id)?.id;

        return {
          objectId: this.tagModelId,
          model: this.tagModel,
          createdUserId: Number(this.$rsGlobals.$currentUser.userId),
          tagId: existingTag?.id || 0,
          tag: existingTag ?? {
            id: undefined,
            name: v.value.trim(),
            clientId: this.$rsGlobals.$currentClient.id!,
            siteId,
            isCategory: 0,
            createdUserId: Number(this.$rsGlobals.$currentUser.userId),
          },
        } as IObjectsHasTags;
      });

      this.handleInputFocus();
    },

    async handleSave() {
      const tagsToAdd = this.objectHasTags.filter((oht) => !oht.tag?.id).map((oht) => oht.tag);
      const tagsAdded = tagsToAdd.length > 0
        ? (await adminRequest.post<ITags[]>(`/website-api/tags?siteId=${this.$rsGlobals.$siteSourceClientId ?? null}`, tagsToAdd)).data ?? []
        : [];

      const objectHasTagsToSave = this.objectHasTags
        .filter((oht) => !oht.id)
        .map((oht) => {
          const objectHasTag = { ...oht };
          objectHasTag.tagId = oht.tagId > 0
            ? oht.tagId
            : tagsAdded.find((t) => t.name === oht.tag?.name)?.id || 0;
          delete objectHasTag.tag;
          return objectHasTag;
        });

      if (objectHasTagsToSave.length > 0) {
        await adminRequest.post<ITags[]>('/website-api/objects_has_tags', objectHasTagsToSave);
      }

      if (this.removeList.length > 0) {
        await adminRequest.delete('/website-api/objects_has_tags', this.removeList.map((t) => t.id ?? 0));
        this.removeList = [];
      }

      await this.init();
      return this.objectHasTags;
    },
  },
});
</script>

<style scoped lang="css">
.unselected-tag {
  cursor: pointer;
  border-radius: 4px;
  padding: 3px;
  margin: 3px 0px;
}
.unselected-tag:hover {
  background: #e8e7e7;
}
</style>
